import React from "react";
import {Button, Col, DatePicker, Form, Input, message, Row, Select} from "antd";
import dayjs from "dayjs";
import {Blood_Group, Gender, Marital_Status} from "../../../../constant/constant";
import {IPrimaryDetailFormModel} from "./types/primary-detail.types";
import EmployeeSelect from "../../../common/employee-select/employee-select";
import Locale from "../../../../core/locale/locale";

export interface IPrimaryDetailViewProps {
    loading: boolean;
    goToNextTab: () => void;
    viewModel: IPrimaryDetailFormModel;
    onSubmit: (values: IPrimaryDetailFormModel) => Promise<void>;
    employeeId?: string;
}

const PrimaryDetailView = (props: IPrimaryDetailViewProps) => {
    const [form] = Form.useForm();

    const handleResetFields = () => {
        form.resetFields();
        form.setFieldsValue([]);
    }

    async function handleFormFinish() {
        await form.validateFields()
            .then((formValues: IPrimaryDetailFormModel) => {
                props.onSubmit(formValues);
            })
            .catch(() => message.error("Please fill all the required fields"));
    }

    const handleValuesChange = (updatedValues: IPrimaryDetailFormModel) => {
        if (updatedValues.dateOfBirth) {
            const dob = updatedValues.dateOfBirth;
            if (dob) {
                const birthDate = dayjs(dob);
                const today = dayjs();
                let age = today.year() - birthDate.year();
                if (
                    today.month() < birthDate.month() ||
                    (today.month() === birthDate.month() && today.date() < birthDate.date())
                ) {
                    age--;
                }
                form.setFieldsValue({age});
            } else {
                form.setFieldsValue({age: undefined});
            }
        }
    };

    return (
        <section className={'hrms__employee-create__form primary-details'}>
            <div className={'formHeadings'}>
                <h2>Primary Details</h2>
            </div>
            <Form
                layout={'vertical'}
                form={form}
                initialValues={props.viewModel}
                onValuesChange={(changed) => handleValuesChange(changed)}
                onFinishFailed={({errorFields}) => {
                    form.scrollToField(errorFields[0].name[0]);
                }}
            >
                <div className={'personalDetailsBox'}>
                    {/* userId */}
                    <Form.Item
                        label={"User Id"}
                        name={"userId"}
                        className={"formInputFields"}
                        hidden={true}
                    >
                        <Input readOnly={true}/>
                    </Form.Item>

                    {/* First, Middle and Last Name */}
                    <Row gutter={15}>
                        <Col span={5}>
                            <Form.Item
                                name={"firstName"}
                                label={"First Name"}
                                className={"formInputFields"}
                                required={true}
                                rules={[
                                    {
                                        required: true,
                                        message: "Please enter first name",
                                    },
                                    () => ({
                                        validator(_, value) {
                                            if (!value) {
                                                return Promise.reject();
                                            }
                                            const regex = /^[A-Za-z\s]+$/;
                                            if (!regex.test(value)) {
                                                return Promise.reject(
                                                    "First Name should only contain alphabets"
                                                );
                                            }
                                            return Promise.resolve();
                                        },
                                    }),
                                ]}
                            >
                                <Input placeholder={'Enter first name'}/>
                            </Form.Item>
                        </Col>
                        <Col span={5}>
                            <Form.Item
                                name={"middleName"}
                                label={"Middle Name"}
                                className={"formInputFields"}
                            >
                                <Input placeholder={'Enter middle name'}/>
                            </Form.Item>
                        </Col>
                        <Col span={5}>
                            <Form.Item
                                name={"lastName"}
                                label={"Last Name"}
                                className={"formInputFields"}
                            >
                                <Input placeholder={'Enter last name'}/>
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* Date of Birth, Age and Blood Group */}
                    <Row gutter={15}>
                        <Col span={5}>
                            <Form.Item
                                label="Date of Birth"
                                name={"dateOfBirth"}
                                className={"formInputFields"}
                                rules={[
                                    {
                                        validator: (_, value) => {
                                            if (!value) {
                                                return Promise.resolve();
                                            }
                                            const today = new Date();
                                            const birthDate = new Date(value.format("YYYY-MM-DD"));
                                            const age =
                                                today.getFullYear() -
                                                birthDate.getFullYear() -
                                                (today < new Date(today.getFullYear(), birthDate.getMonth(), birthDate.getDate()) ? 1 : 0);

                                            if (isNaN(age)) {
                                                return Promise.reject("DOB must be a valid date!");
                                            }
                                            if (age < 18) {
                                                return Promise.reject("Age must be at least 18 years!");
                                            }
                                            if (age > 60) {
                                                return Promise.reject("Age cannot be greater than 60 years!");
                                            }
                                            return Promise.resolve();
                                        },
                                    },
                                ]}
                            >
                                <DatePicker
                                    style={{width: '350px'}}
                                    format={Locale.displayDate}
                                    showTime={false}
                                    disabledDate={(current) =>
                                        current && current > dayjs().endOf("day")
                                    }
                                />

                            </Form.Item>
                        </Col>
                        <Col span={5}>
                            <Form.Item
                                label={"Age"}
                                name={"age"}
                                className={"formInputFields"}
                            >
                                <Input readOnly={true}/>
                            </Form.Item>
                        </Col>
                        <Col span={5}>
                            <Form.Item
                                label="Blood Group"
                                name={"bloodGroup"}
                                className={"formInputFields"}
                            >
                                <Select
                                    style={{width: '350px'}}
                                    virtual={false}
                                    placeholder={'Select blood group'}
                                >
                                    {(Object.keys(Blood_Group) as Array<keyof typeof Blood_Group>).map((key) =>
                                        <Select.Option value={key} key={key}>
                                            {Blood_Group[key]}
                                        </Select.Option>
                                    )}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* Joining Date & Official Email */}
                    <Row gutter={15}>
                        <Col span={8}>
                            <Form.Item
                                label="Joining Date"
                                name={"joiningDate"}
                                className={"formInputFields"}
                                rules={[
                                    {
                                        required: true,
                                        message: 'Please select the Joining Date!',
                                    },
                                ]}
                            >
                                <DatePicker
                                    placeholder={'Select date'}
                                    style={{width: "100%"}}
                                    format={Locale.displayDate}
                                    disabledDate={(current) => current.isAfter(new Date())}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name={"officialEmailId"}
                                label={"Official Email ID"}
                                className={"formInputFields"}
                                rules={[
                                    {
                                        type: 'email',
                                        message: 'The input is not valid E-mail!',
                                    },
                                    {
                                        required: true,
                                        message: 'Please input your E-mail!',
                                    },
                                ]}
                            >
                                <Input placeholder={'Enter official email'}/>
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* Marital & Gender */}
                    <Row gutter={15}>
                        <Col span={8}>
                            <Form.Item
                                label={"Marital Status"}
                                name={"maritalStatus"}
                                className={"formInputFields"}
                            >
                                <Select style={{width: '350px'}} virtual={false}
                                        placeholder={'Select marital status'}>
                                    {(Object.keys(Marital_Status) as Array<keyof typeof Marital_Status>).map((key) =>
                                        <Select.Option value={key} key={key}>
                                            {Marital_Status[key]}
                                        </Select.Option>
                                    )}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                label="Gender"
                                name={"gender"}
                                className={"formInputFields"}
                                required={true}
                                rules={[
                                    {
                                        required: true,
                                        message: "Please select gender",
                                    },
                                ]}
                            >
                                <Select style={{width: '350px'}} virtual={false} placeholder={'Select gender'}>
                                    {(Object.keys(Gender) as Array<keyof typeof Gender>).map((key) =>
                                        <Select.Option value={key} key={key}>
                                            {Gender[key]}
                                        </Select.Option>
                                    )}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    {/*/!* Aadhar Card & Aadhar Card Upload*!/*/}
                    {/*<Row gutter={15}>*/}
                    {/*    <Col span={8}>*/}
                    {/*        <Form.Item*/}
                    {/*            name={"aadharNumber"}*/}
                    {/*            label={"Aadhar Number"}*/}
                    {/*            className={"formInputFields"}*/}
                    {/*            // rules={[*/}
                    {/*            //     {*/}
                    {/*            //         required: true,*/}
                    {/*            //         message: "Please enter Aadhar Number",*/}
                    {/*            //     },*/}
                    {/*            //     {*/}
                    {/*            //         pattern: /^\d{12}$/,*/}
                    {/*            //         message: "Aadhar Number must be a 12-digit number",*/}
                    {/*            //     },*/}
                    {/*            // ]}*/}
                    {/*        >*/}
                    {/*            <Input*/}
                    {/*                style={{width: '350px'}}*/}
                    {/*                maxLength={12}*/}
                    {/*                disabled*/}
                    {/*                placeholder={'Enter Aadhar Number'}*/}
                    {/*            />*/}
                    {/*        </Form.Item>*/}
                    {/*    </Col>*/}
                    {/*    <Col span={5}>*/}
                    {/*        <Form.Item*/}
                    {/*            style={{marginBottom: 'unset'}}*/}
                    {/*            shouldUpdate={(prev, curr) => prev.aadharNumber !== curr.aadharNumber}*/}
                    {/*        >*/}
                    {/*            {({getFieldValue}) => {*/}
                    {/*                const aadharNumberInputValue = getFieldValue('aadharNumber');*/}
                    {/*                const isDisabled = !aadharNumberInputValue || aadharNumberInputValue.trim().length === 0;*/}
                    {/*                return (*/}
                    {/*                    <Form.Item*/}
                    {/*                        label={"Upload Aadhar Card"}*/}
                    {/*                        name={"aadharDocumentId"}*/}
                    {/*                        style={{marginBottom: 'unset'}}*/}
                    {/*                        // rules={[*/}
                    {/*                        //     {*/}
                    {/*                        //         required: true,*/}
                    {/*                        //         message: `Please upload the Aadhaar Card`,*/}
                    {/*                        //     },*/}
                    {/*                        // ]}*/}
                    {/*                    >*/}
                    {/*                        <Document*/}
                    {/*                            disabled={isDisabled}*/}
                    {/*                            docType={'AADHAAR_CARD'}*/}
                    {/*                            docNumber={aadharNumberInputValue}*/}
                    {/*                            purpose={'EMPLOYMENT'}*/}
                    {/*                            employeeId={props.employeeId}*/}
                    {/*                        />*/}
                    {/*                    </Form.Item>*/}
                    {/*                )*/}
                    {/*            }}*/}
                    {/*        </Form.Item>*/}
                    {/*    </Col>*/}
                    {/*</Row>*/}

                    {/*/!* Pan Card & Pan Card Upload*!/*/}
                    {/*<Row gutter={15}>*/}
                    {/*    <Col span={8}>*/}
                    {/*        <Form.Item*/}
                    {/*            name={"panNumber"}*/}
                    {/*            label={"PAN Number"}*/}
                    {/*            className={"formInputFields"}*/}
                    {/*            // rules={[*/}
                    {/*            //     {*/}
                    {/*            //         required: true,*/}
                    {/*            //         message: "Please enter PAN Number",*/}
                    {/*            //     }*/}
                    {/*            // ]}*/}
                    {/*        >*/}
                    {/*            <Input*/}
                    {/*                style={{width: '350px'}}*/}
                    {/*                maxLength={12}*/}
                    {/*                disabled*/}
                    {/*                placeholder={'Enter PAN Number'}*/}
                    {/*            />*/}
                    {/*        </Form.Item>*/}
                    {/*    </Col>*/}
                    {/*    <Col span={5}>*/}
                    {/*        <Form.Item*/}
                    {/*            style={{marginBottom: 'unset'}}*/}
                    {/*            shouldUpdate={(prev: any, curr: any) => prev.panNumber !== curr.panNumber}*/}
                    {/*        >*/}
                    {/*            {({getFieldValue}) => {*/}
                    {/*                const panNumberInputValue = getFieldValue('panNumber');*/}
                    {/*                const isDisabled = !panNumberInputValue || panNumberInputValue.trim().length === 0;*/}
                    {/*                return (*/}
                    {/*                    <Form.Item*/}
                    {/*                        label={"Upload PAN Card"}*/}
                    {/*                        name={"panDocumentId"}*/}
                    {/*                        style={{marginBottom: 'unset'}}*/}
                    {/*                        // rules={[*/}
                    {/*                        //     {*/}
                    {/*                        //         required: true,*/}
                    {/*                        //         message: `Please upload the Pan Card`,*/}
                    {/*                        //     },*/}
                    {/*                        // ]}*/}
                    {/*                    >*/}
                    {/*                        <Document*/}
                    {/*                            disabled={isDisabled}*/}
                    {/*                            docType={'PAN_CARD'}*/}
                    {/*                            docNumber={panNumberInputValue}*/}
                    {/*                            purpose={'EMPLOYMENT'}*/}
                    {/*                            employeeId={props.employeeId}*/}
                    {/*                        />*/}
                    {/*                    </Form.Item>*/}
                    {/*                )*/}
                    {/*            }}*/}
                    {/*        </Form.Item>*/}
                    {/*    </Col>*/}
                    {/*</Row>*/}

                    {/* Reporting Manager & Employee Code */}
                    <Row gutter={15}>
                        <Col span={8}>
                            <Form.Item
                                label={"Reporting Manager"}
                                name={"reportingId"}
                                className={"formInputFields"}
                            >
                                <EmployeeSelect
                                    includeAllSelectOption={false}
                                    placeholder={'Select reporting manager'}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name={"employeeCode"}
                                label={"Employee Code"}
                                className={"formInputFields"}
                                required={true}
                                rules={[
                                    {
                                        required: true,
                                        message: "Please enter Employee Code",
                                    },
                                ]}
                            >
                                <Input placeholder={'Enter employee code'} style={{width: '350px'}}/>
                            </Form.Item>
                        </Col>
                    </Row>
                </div>
            </Form>

            {/* Floating Buttons */}
            <div className={'pn__employee-create__form-actions'}>
                <Button
                    danger
                    size={'large'}
                    disabled={props.loading}
                    onClick={handleResetFields}
                    className={'reset-button'}
                >
                    Reset
                </Button>
                <Button
                    type="primary"
                    size={'large'}
                    disabled={props.loading}
                    onClick={handleFormFinish}
                    className={'submit-button'}
                >
                    Submit
                </Button>
            </div>
        </section>
    )
}

export default PrimaryDetailView;
